/**
 * Port of C# GenerateHtmlJson.cs
 * HTML to JSON conversion logic
 * New format uses type prefixes: s=Html, k=JsonKey, p=Placeholder, h=Slotholder, a=ArrayBlock, x=SlotHtml, i=Slot
 * Uppercase prefix indicates skip marker: K=JsonKeySkip, P=PlaceholderSkip, H=SlotholderSkip, A=ArrayBlockSkip
 * Each structure includes _meta for O(1) lookup optimization
 */

// Type prefixes for the new format are declared alongside the parser that emits them
// (generateHtmlJson_Parse.ts), mirroring C# GenerateHtmlJson.cs : private const char TYPE_*.

export const META_KEY = '_meta';

// #region Metadata Helper

export interface HtmlJsonMeta {
    hasJsonKeys: boolean;
    jsonKeys: string[];
    hasPlaceholders: boolean;
    placeholders: string[];
    hasSlotholders: boolean;
    slotholders: string[];
    hasArrayBlocks: boolean;
    arrayBlocks: string[];
}

// #endregion

import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { splitByPlaceholders } from './loaderJsonHelper.js';

// #region Public Helpers for Meta Access

/**
 * Extracts a string array from a JsonArray stored in a JsonObject field
 */
function getStringArray(metaObj: JsonObject, field: string): string[] {
    const arrValue = metaObj.get(field);
    const arr = arrValue.asArray();
    if (!arr) return [];
    const result: string[] = [];
    for (let i = 0; i < arr.length; i++) {
        const s = arr[i].asString();
        if (s !== null) result.push(s);
    }
    return result;
}

/**
 * Extracts HtmlJsonMeta from a JsonObject's _meta entry
 */
function getHtmlJsonMeta(htmlJson: JsonObject): HtmlJsonMeta | null {
    const metaValue = htmlJson.get(META_KEY);
    if (metaValue.isNull) return null;
    const metaObj = metaValue.asObject();
    if (!metaObj) return null;
    return {
        hasJsonKeys: metaObj.get('hasJsonKeys').asBool() === true,
        jsonKeys: getStringArray(metaObj, 'jsonKeys'),
        hasPlaceholders: metaObj.get('hasPlaceholders').asBool() === true,
        placeholders: getStringArray(metaObj, 'placeholders'),
        hasSlotholders: metaObj.get('hasSlotholders').asBool() === true,
        slotholders: getStringArray(metaObj, 'slotholders'),
        hasArrayBlocks: metaObj.get('hasArrayBlocks').asBool() === true,
        arrayBlocks: getStringArray(metaObj, 'arrayBlocks')
    };
}

// #endregion

// Re-export from split files
export { convertJsonToHtml } from './generateHtmlJson_Convert.js';

import { generateHtmlJsonParseMethods } from './generateHtmlJson_Parse.js';
import { convertJsonToHtml } from './generateHtmlJson_Convert.js';

export class GenerateHtmlJson {
    static addArrayBlockToMeta(meta: HtmlJsonMeta, name: string): void {
        meta.hasArrayBlocks = true;
        if (!meta.arrayBlocks.includes(name)) {
            meta.arrayBlocks.push(name);
        }
    }

    static addJsonKeyToMeta(meta: HtmlJsonMeta, keyName: string): void {
        meta.hasJsonKeys = true;
        if (!meta.jsonKeys.includes(keyName)) {
            meta.jsonKeys.push(keyName);
        }
    }

    static addPlaceholderToMeta(meta: HtmlJsonMeta, name: string): void {
        meta.hasPlaceholders = true;
        if (!meta.placeholders.includes(name)) {
            meta.placeholders.push(name);
        }
    }

    static addSlotholderToMeta(meta: HtmlJsonMeta, name: string): void {
        meta.hasSlotholders = true;
        if (!meta.slotholders.includes(name)) {
            meta.slotholders.push(name);
        }
    }

    static createMeta(): HtmlJsonMeta {
        return {
            hasJsonKeys: false,
            jsonKeys: [],
            hasPlaceholders: false,
            placeholders: [],
            hasSlotholders: false,
            slotholders: [],
            hasArrayBlocks: false,
            arrayBlocks: []
        };
    }

    static getKeyName(key: string): string {
        const underscoreIdx = key.indexOf('_');
        return underscoreIdx >= 0 ? key.substring(underscoreIdx + 1) : '';
    }

    static getKeyType(key: string): string {
        return key && key.length > 0 ? key[0] : '';
    }

    static hasArrayBlocks(htmlJson: JsonObject): boolean {
        const meta = getHtmlJsonMeta(htmlJson);
        return meta ? meta.hasArrayBlocks === true : false;
    }

    static hasJsonKeys(htmlJson: JsonObject): boolean {
        const meta = getHtmlJsonMeta(htmlJson);
        return meta ? meta.hasJsonKeys === true : false;
    }

    static hasPlaceholders(htmlJson: JsonObject): boolean {
        const meta = getHtmlJsonMeta(htmlJson);
        return meta ? meta.hasPlaceholders === true : false;
    }

    static hasSlotholders(htmlJson: JsonObject): boolean {
        const meta = getHtmlJsonMeta(htmlJson);
        return meta ? meta.hasSlotholders === true : false;
    }

    static jsonArrayContainsString(values: string[], value: string): boolean {
        for (let i = 0; i < values.length; i++) {
            if (values[i] === value) {
                return true;
            }
        }
        return false;
    }

    static getJsonKeyNames(htmlJson: JsonObject): string[] | null {
        const meta = getHtmlJsonMeta(htmlJson);
        if (meta && meta.jsonKeys && Array.isArray(meta.jsonKeys)) {
            return meta.jsonKeys;
        }

        return null;
    }

    static getPlaceholderNames(htmlJson: JsonObject): string[] | null {
        const meta = getHtmlJsonMeta(htmlJson);
        if (meta && meta.placeholders && Array.isArray(meta.placeholders)) {
            return meta.placeholders;
        }

        return null;
    }

    static isNewFormat(htmlJson: JsonObject): boolean {
        return htmlJson && htmlJson.has(META_KEY);
    }

    static convertHtmlToJson(htmlContent: string): { content: JsonObject } {
        const parts: string[] = splitByPlaceholders(htmlContent);
        const meta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
        const { result } = generateHtmlJsonParseMethods.parseTemplateContentNew(parts, 0, meta);

        const finalResult = new JsonObject();
        finalResult.set(META_KEY, meta);
        for (const key of Object.keys(result)) {
            finalResult.set(key, result[key]);
        }

        return { content: finalResult };
    }

    static convertJsonToHtml = convertJsonToHtml;
}
